import React, { useState, useEffect } from 'react';
import { Zap, TrendingUp, Target, Wallet, ChevronDown, Sparkles, Users, DollarSign, Trophy, Star, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { testimonials, benefits, faqs, impactStats, allianzProducts, challengeCategories } from '../data/mockData';
import { createParticles } from '../utils/particles';
import { floatingIcons, brandLogos } from '../utils/floatingElements';
import { analytics } from '../services/analytics';
import InstitutionRecommendations from '../components/landing/InstitutionRecommendations';
import RegulatorsBadge from '../components/landing/RegulatorsBadge';
import OptimaxxTimeline from '../components/landing/OptimaxxTimeline';
import OptimaxxComparator from '../components/landing/OptimaxxComparator';
import OptimaxxSummary from '../components/landing/OptimaxxSummary';
import TestimonialsSection from '../components/landing/TestimonialsSection';
import FAQSection from '../components/landing/FAQSection';
import EmailCaptureSection from '../components/landing/EmailCaptureSection';
import ShareButtons from '../components/landing/ShareButtons';
import ChallengesSection from '../components/landing/ChallengesSection';
import ToolsSection from '../components/landing/ToolsSection';
import FloatingNav from '../components/landing/FloatingNav';
import InstagramBanner from '../components/landing/InstagramBanner';
import AppShowcaseSection from '../components/landing/AppShowcaseSection';
import { useToolsState } from '../hooks/useToolsState';
import { useEmailSubmit } from '../hooks/useEmailSubmit';
import '../styles/landing.css';

const LandingPage = () => {
  const [animatedStats, setAnimatedStats] = useState(impactStats.map(stat => ({ ...stat, current: 0 })));

  // Lead capture form (name + phone + email)
  const { form: leadForm, loading: leadLoading, handleSubmit: handleLeadSubmit, handleChangeField } = useEmailSubmit();

  // 30-day challenge state (multi-select)
  const [challenge30, setChallenge30] = useState({
    selectedCategories: [], // array of category ids
    customAmount: '',
    dailySavings: 0,
    monthlySavings: 0,
    yearlySavings: 0
  });

  // 18-month challenge state
  const [challenge18, setChallenge18] = useState({
    monthlyAmount: '',
    totalSavings: 0,
    withInterest: 0,
    rate: 8
  });

  // Tools state (test, índice, goal, growth) — encapsulated in custom hook
  const tools = useToolsState({ challenge18, setChallenge18 });

  // Particles for background
  const [particles] = useState(createParticles());

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    if (id === 'email-capture') {
      analytics.ctaClick('scroll_to_email_capture');
    }
  };

  // Animated counter for impact stats
  useEffect(() => {
    const duration = 2000;
    const steps = 60;
    const incrementTime = duration / steps;

    const timers = impactStats.map((stat, index) => {
      let currentValue = 0;
      const targetValue = stat.value;
      const stepSize = Math.ceil(targetValue / steps);

      return setInterval(() => {
        currentValue = Math.min(currentValue + stepSize, targetValue);
        
        setAnimatedStats(prev => {
          const newStats = [...prev];
          newStats[index] = { ...stat, current: currentValue };
          return newStats;
        });

        if (currentValue >= targetValue) {
          // Timer will be cleared in cleanup
        }
      }, incrementTime);
    });

    return () => timers.forEach(timer => clearInterval(timer));
  }, [impactStats]); // Dependency on impactStats data

  // 30-day challenge functions — multi-select (toggle category in/out)
  // The custom input always mirrors the SUM of selected categories.
  // The user can still override it manually before calculating.
  const selectChallengeCategory = (category) => {
    setChallenge30((prev) => {
      const isSelected = prev.selectedCategories.includes(category.id);
      const newSelected = isSelected
        ? prev.selectedCategories.filter((id) => id !== category.id)
        : [...prev.selectedCategories, category.id];

      const total = newSelected.reduce((sum, id) => {
        const cat = challengeCategories.find((c) => c.id === id);
        return sum + (cat ? cat.avgCost : 0);
      }, 0);

      return {
        ...prev,
        selectedCategories: newSelected,
        customAmount: total > 0 ? String(total) : '',
        dailySavings: total,
        monthlySavings: total * 30,
        yearlySavings: total * 30 * 12
      };
    });
  };

  const calculateCustomChallenge = () => {
    const daily = parseFloat(challenge30.customAmount);
    if (!daily || daily <= 0) {
      toast.error('Selecciona al menos un gasto o ingresa un monto diario');
      return;
    }

    setChallenge30((prev) => ({
      ...prev,
      dailySavings: daily,
      monthlySavings: daily * 30,
      yearlySavings: daily * 30 * 12
    }));

    const tag = challenge30.selectedCategories.length > 0
      ? challenge30.selectedCategories.join(',')
      : 'custom';
    analytics.accept30DayChallenge(tag);
    toast.success('¡Reto calculado! Imagina todo lo que podrías ahorrar');
  };

  // 18-month challenge function
  const calculate18MonthChallenge = () => {
    if (!challenge18.monthlyAmount) {
      toast.error('Ingresa tu aporte mensual');
      return;
    }

    const monthly = parseFloat(challenge18.monthlyAmount);
    const rate = parseFloat(challenge18.rate) / 100 / 12;
    const months = 18;

    // Calculate with compound interest
    const withInterest = monthly * ((Math.pow(1 + rate, months) - 1) / rate) * (1 + rate);
    const withoutInterest = monthly * months;

    setChallenge18({
      ...challenge18,
      totalSavings: withoutInterest,
      withInterest: withInterest
    });

    analytics.accept18MonthChallenge();
    toast.success('¡Increíble! Mira lo que lograrías en 18 meses');
  };

  return (
    <div className="landing-container">
      {/* Floating quick-nav (sticky side rail / mobile drawer) */}
      <FloatingNav />

      {/* Background Elements */}
      <div className="bg-orb bg-orb-1"></div>
      <div className="bg-orb bg-orb-2"></div>
      <div className="bg-orb bg-orb-3"></div>
      <div className="bg-lines">
        <div className="bg-line bg-line-1"></div>
        <div className="bg-line bg-line-2"></div>
        <div className="bg-line bg-line-3"></div>
      </div>
      <div className="noise-overlay"></div>
      
      {/* Floating Particles */}
      <div className="particles-container">
        {particles.map(particle => (
          <div
            key={particle.id}
            className="particle"
            style={{
              left: `${particle.x}%`,
              top: `${particle.y}%`,
              width: `${particle.size}px`,
              height: `${particle.size}px`,
              animationDuration: `${particle.duration}s`,
              animationDelay: `${particle.delay}s`
            }}
          />
        ))}
      </div>

      {/* Floating Sarcastic Icons */}
      <div className="floating-icons-container">
        {floatingIcons.map(icon => (
          <div
            key={icon.id}
            className="floating-icon"
            style={{
              left: `${icon.x}%`,
              top: `${icon.y}%`,
              animationDuration: `${icon.duration}s`,
              animationDelay: `${icon.delay}s`
            }}
          >
            <span className="icon-emoji">{icon.emoji}</span>
            <span className="icon-message">{icon.message}</span>
          </div>
        ))}
      </div>

      {/* Floating Brand Logos */}
      <div className="floating-logos-container">
        {brandLogos.map(logo => (
          <div
            key={logo.id}
            className="floating-logo"
            style={{
              left: `${logo.x}%`,
              top: `${logo.y}%`,
              fontSize: `${logo.size}px`,
              animationDuration: `${logo.duration}s`,
              animationDelay: `${logo.delay}s`,
              '--rotation': `${logo.rotation}deg`
            }}
          >
            {logo.letter}
          </div>
        ))}
      </div>

      {/* Header */}
      <header className="header">
        <div className="header-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_ahorro-constante/artifacts/if3q1xmz_QUITAMELO_PORQUE_ME_LO_GASTO_6.png" 
            alt="Quitamelo Logo" 
            className="logo"
          />
          <nav className="nav">
            <button onClick={() => scrollToSection('como-funciona')} className="nav-link">Cómo funciona</button>
            <button onClick={() => scrollToSection('calculadora')} className="nav-link">Calculadora</button>
            <button onClick={() => scrollToSection('faq')} className="nav-link">FAQ</button>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section className="hero">
        <div className="hero-content">
          <div className="hero-badge">
            <Sparkles className="w-4 h-4" />
            <span>Porque "mañana empiezo" nunca llega</span>
          </div>
          <h1 className="hero-title">
            ¡Quítamelo Porque <span className="neon-text">Me Lo Gasto!</span>
          </h1>
          <p className="hero-subtitle">
            Automatiza tu ahorro antes de que encuentres en qué gastarlo. 
            Porque seamos honestos: si el dinero está ahí, se esfuma solo 💸
          </p>

          {/* What this site actually is — 3 pillars, scannable in 5 seconds */}
          <div className="hero-pillars" data-testid="hero-pillars">
            <p className="hero-pillars-intro">
              En pocas palabras:
            </p>
            <div className="hero-pillars-grid">
              <div className="hero-pillar" data-testid="hero-pillar-humor">
                <span className="hero-pillar-emoji" aria-hidden="true">😏</span>
                <div className="hero-pillar-body">
                  <h3 className="hero-pillar-title">Humor financiero sarcástico</h3>
                  <p className="hero-pillar-text">
                    Educación financiera sin aburrirte. Cero corbatas, cero PowerPoints. Sólo verdades incómodas y memes que te hacen pensar.
                  </p>
                </div>
              </div>
              <div className="hero-pillar" data-testid="hero-pillar-tools">
                <span className="hero-pillar-emoji" aria-hidden="true">🧮</span>
                <div className="hero-pillar-body">
                  <h3 className="hero-pillar-title">Herramientas que sí usas</h3>
                  <p className="hero-pillar-text">
                    Calculadoras interactivas para metas, índice de ahorro, interés compuesto y retos gamificados. Llenas, ves números reales, decides.
                  </p>
                </div>
              </div>
              <div className="hero-pillar" data-testid="hero-pillar-recommend">
                <span className="hero-pillar-emoji" aria-hidden="true">🎯</span>
                <div className="hero-pillar-body">
                  <h3 className="hero-pillar-title">Te decimos dónde automatizar</h3>
                  <p className="hero-pillar-text">
                    Te recomendamos instituciones reguladas (CNSF/Condusef) para que automatices tu ahorro o inversión <strong>antes</strong> de encontrar dónde gastarlo.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="hero-cta">
            <Button 
              size="lg" 
              className="cta-button cta-button-primary"
              onClick={() => scrollToSection('conoce-la-app')}
              data-testid="hero-cta-app"
            >
              Descargar la app · 1 mes gratis
              <ChevronDown className="ml-2 w-5 h-5 animate-bounce" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="cta-button cta-button-secondary"
              onClick={() => scrollToSection('email-capture')}
              data-testid="hero-cta-advisor"
            >
              Prefiero asesoría personal
            </Button>
          </div>
        </div>
        <div className="hero-visual">
          <div className="floating-card card-1">
            <TrendingUp className="w-8 h-8 text-neon" />
            <div>
              <p className="text-sm text-gray-400">Crecimiento anual</p>
              <p className="text-2xl font-bold text-neon">+12%</p>
            </div>
          </div>
          <div className="floating-card card-2">
            <Wallet className="w-8 h-8 text-neon" />
            <div>
              <p className="text-sm text-gray-400">Ahorro automático</p>
              <p className="text-2xl font-bold text-neon">$5,000</p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Statistics */}
      <section className="section stats-section">
        <div className="stats-growth-badge">
          <Sparkles className="w-4 h-4" />
          <span>En crecimiento — sí, somos nuevos. Tú también, en esto del ahorro 😏</span>
        </div>
        <div className="stats-grid">
          {animatedStats.map((stat) => {
            const Icon = stat.id === 1 ? Users :
                        stat.id === 2 ? DollarSign :
                        stat.id === 3 ? Trophy : Star;
            return (
              <div key={stat.id} className="stat-card">
                <Icon className="stat-icon" />
                <div className="stat-value">
                  {stat.prefix || ''}{stat.current.toLocaleString()}{stat.suffix || ''}
                </div>
                <div className="stat-label">{stat.label}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="section" id="como-funciona">
        <div className="section-header">
          <h2 className="section-title">¿Cómo funciona?</h2>
          <p className="section-subtitle">4 pasos para dejar de ser víctima de tus propios impulsos 🎯</p>
        </div>
        <div className="benefits-grid">
          {benefits.map((benefit, index) => {
            const Icon = benefit.icon === 'Zap' ? Zap : 
                        benefit.icon === 'TrendingUp' ? TrendingUp :
                        benefit.icon === 'Target' ? Target : Wallet;
            return (
              <div key={benefit.id} className="benefit-card">
                <div className="benefit-number">{index + 1}</div>
                <Icon className="w-12 h-12 text-neon mb-4" />
                <h3 className="benefit-title">{benefit.title}</h3>
                <p className="benefit-description">{benefit.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Conoce la App — SaaS product showcase */}
      <AppShowcaseSection />

      {/* Interactive Tools Section */}
      <ToolsSection
        {...tools}
        scrollToSection={scrollToSection}
      />

      {/* Challenges Section */}
      <ChallengesSection
        challenge30={challenge30}
        setChallenge30={setChallenge30}
        challenge18={challenge18}
        setChallenge18={setChallenge18}
        selectChallengeCategory={selectChallengeCategory}
        calculateCustomChallenge={calculateCustomChallenge}
        calculate18MonthChallenge={calculate18MonthChallenge}
        scrollToSection={scrollToSection}
      />

      {/* Allianz Products Section */}
      <section className="section section-dark" id="productos">
        <div className="section-header">
          <h2 className="section-title">Nuestro Producto Recomendado</h2>
          <p className="section-subtitle">Porque necesitas a alguien que te obligue a ahorrar (con respaldo serio) 🛡️</p>
        </div>

        <RegulatorsBadge />

        <OptimaxxTimeline />

        <OptimaxxComparator onCtaClick={() => scrollToSection('email-capture')} />

        {/* Compact summary footer — consolidates min amount + ideal-for + CTA */}
        {allianzProducts.map((product) => (
          <OptimaxxSummary
            key={product.id}
            product={product}
            onCtaClick={() => scrollToSection('email-capture')}
          />
        ))}
      </section>

      {/* Testimonials */}
      <TestimonialsSection testimonials={testimonials} />

      {/* Instagram Banner — follow CTA between testimonials and FAQ */}
      <InstagramBanner />

      <FAQSection faqs={faqs} />

      <EmailCaptureSection
        form={leadForm}
        loading={leadLoading}
        onFieldChange={handleChangeField}
        onSubmit={handleLeadSubmit}
      />

      {/* Footer */}
      <footer className="footer">
        <div className="footer-content">
          <img 
            src="https://customer-assets.emergentagent.com/job_ahorro-constante/artifacts/if3q1xmz_QUITAMELO_PORQUE_ME_LO_GASTO_6.png" 
            alt="Quitamelo Logo" 
            className="footer-logo"
          />
          <p className="footer-disclaimer">
            Las estrategias presentadas pueden implementarse mediante productos ofrecidos por 
            instituciones financieras reguladas en México. Algunas soluciones pueden ser distribuidas 
            a través de aliados comerciales como Allianz México. La inversión implica riesgos y los 
            rendimientos no están garantizados. Esta plataforma no capta recursos directamente del público.
          </p>
          <div className="footer-links" style={{ marginTop: '1rem', marginBottom: '1rem' }}>
            <a 
              href="/aviso-de-privacidad" 
              className="text-neon hover:underline text-sm"
              style={{ transition: 'all 0.3s ease' }}
            >
              Aviso de Privacidad
            </a>
          </div>
          <p className="footer-copyright">
            © 2026 Quítamelo Porque Me Lo Gasto. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
